import { useCallback } from 'react';
import {createShapeId, Editor} from '@tldraw/tldraw';
import { TLAsset, TLShapeId, TLAssetId, TLImageShape, TLFrameShape, TLShape, TLParentId } from '@tldraw/tlschema';
import { AssetRecordType } from '@tldraw/tldraw';
import {FrameShapeProps} from "../types/draw.types";
import {Constant} from "../types/constant";

interface ImageItem {
  url: string;
  width?: number;
  height?: number;
}

interface WaterfallLayoutOptions {
  columnCount?: number;
  columnGap?: number;
  offsetX?: number;
  offsetY?: number;
  maxImageWidth?: number;
  framePadding?: number;
}

interface ImageAsset {
  id: TLAssetId;
  type: string;
  typeName: string;
  props: {
    name: string;
    src: string;
    w: number;
    h: number;
    mimeType: string;
    isAnimated: boolean;
  };
  meta: Record<string, unknown>;
  position?: {
    x: number;
    y: number;
  };
}

export const useWaterfallLayout = (editor: Editor | undefined) => {
  /**
   * 创建瀑布流布局
   * @param images 选中的图片索引数组
   * @param imageData 图片数据源
   * @param baseUrl 图片URL前缀
   * @param options 布局选项
   */
  const createWaterfallLayout = useCallback(
    async (
      images: number[], 
      imageData: ImageItem[], 
      baseUrl: string = '',
      options: WaterfallLayoutOptions = {},
      label: string = '图片瀑布流',
      setShapeId: (id: string, shapeProps: FrameShapeProps) => void,
      currentTaskFrameIds: {[key: string]: FrameShapeProps} | null,
    ) => {
      if (!editor) return false;
      
      // 用于跟踪需要清理的资源ID
      const createdAssetIds: TLAssetId[] = [];
      const createdShapeIds: TLShapeId[] = [];
      let frameId: TLShapeId | null = null;
      let checkImagesLoadedTimer: NodeJS.Timeout | null = null;

      try {
        // 找到画布中所有形状的最低点
        const shapes = editor.getCurrentPageShapes();
        let lowestY = 0;
        let leftX = Infinity;  // 初始化为最大值
        let rightX = -Infinity;  // 初始化为最小值

        shapes.forEach((shape) => {
          if (shape.type === 'frame') {
            const frameShape = shape as TLFrameShape;
            // 更新最低点
            lowestY = Math.max(lowestY, frameShape.y + frameShape.props.h);
            // 更新最左和最右的 x 坐标
            leftX = Math.min(leftX, frameShape.x);
            rightX = Math.max(rightX, frameShape.x + frameShape.props.w);
          }
        });

        // 如果没有找到任何 frame，使用默认值
        if (leftX === Infinity) leftX = 0;
        if (rightX === -Infinity) rightX = 0;

        // 计算中心点：最左和最右 frame 的中心
        const centerX = (leftX + rightX) / 2;

        // 添加一些间距
        const verticalGap = Constant.FrameVerticalGap; // 与上一个元素的间距
        
        // 更新起始 Y 坐标
        const {
          columnCount = Constant.GenerateImagesColumn,
          columnGap = Constant.FrameColumnGap,
          framePadding = Constant.FramePadding,
          maxImageWidth = Constant.MaxImageWidth
        } = options;

        // 使用找到的最低点作为新的起始Y坐标
        let startY = lowestY + verticalGap;
        let startX = centerX - (columnCount * maxImageWidth + (columnCount - 1) * columnGap) / 2; // 将新 frame 的中心对齐到计算出的中心点

        if (currentTaskFrameIds) { // 如果不为空，则y轴不变，所有队列中的x轴向右偏移
            const frameIds = Object.keys(currentTaskFrameIds);
            const firstFrame = currentTaskFrameIds[frameIds[0]];
            const lastFrame = currentTaskFrameIds[frameIds[frameIds.length - 1]];

            const moveOffset = ((columnCount * maxImageWidth) / 2) + framePadding * 2 - (columnCount - 1) * columnGap

            startY = firstFrame.y + framePadding;
            startX = lastFrame.x + lastFrame.props.w + 60 - moveOffset;

            // 将每个 shape 向左移动新增列的二分之一
            const updates = frameIds.map(frameId => {
              //  const shape = currentTaskFrameIds[frameId];
                const shape = editor.getShape(frameId as TLShapeId);
                if (shape) {
                    return {
                        id: frameId as TLShapeId,
                        type: shape.type,
                        x: shape.x - moveOffset,
                        y: shape.y,
                        props: shape.props
                    };
                }
                return null;
            }).filter(Boolean);

            if (updates.length > 0) {
                editor.updateShapes(updates);
            }
        }

        // 更新 columnHeights 初始值
        const columnHeights = new Array(columnCount).fill(startY);

        // 查找画布中的视口范围
        const bounds = editor.getViewportPageBounds();
        
        // 瀑布流布局设置
        const columnWidths = new Array(columnCount).fill(maxImageWidth);
        
        // 处理图片资源 - 优化：使用Promise.all并行处理图片
        const loadImages = images.map((index) => {
          const imgItem = imageData[index];
          if (!imgItem?.url) {
            console.warn(`Missing image data at index ${index}`);
            return null;
          }
          
          const assetId = AssetRecordType.createId();
          createdAssetIds.push(assetId);
          
          // 计算图片缩放比例
          const coefficient = maxImageWidth / (imgItem?.width || 1200);
          
          // 根据图片比例确定显示尺寸
          const originalWidth = (imgItem?.width || 1200) * coefficient;
          const originalHeight = (imgItem?.height || 675) * coefficient;
          
          return {
            id: assetId,
            type: 'image',
            typeName: 'asset',
            props: {
              name: imgItem?.url,
              src: baseUrl + imgItem?.url,
              w: originalWidth,
              h: originalHeight,
              mimeType: 'image/png',
              isAnimated: true,
            },
            meta: {},
          } as ImageAsset;
        }).filter(Boolean) as ImageAsset[];
        
        if (loadImages.length === 0) {
          console.warn('No valid images to display');
          return false;
        }

        // 创建资源
        editor.createAssets(loadImages as TLAsset[]);

        // 计算框架容器的总宽度
        const totalWidth = columnCount * maxImageWidth + (columnCount - 1) * columnGap;
        
        // 计算图片位置
        const positionedImages = loadImages.map(img => {
          // 找到当前高度最小的列
          const minHeightColumnIndex = columnHeights.indexOf(Math.min(...columnHeights));
          
          // 计算图片位置
          const x = startX + minHeightColumnIndex * (maxImageWidth + columnGap);
          const y = columnHeights[minHeightColumnIndex];
          
          // 更新该列的高度
          columnHeights[minHeightColumnIndex] += img.props.h + 20; // 20 是图片之间的垂直间距
          
          return {
            ...img,
            position: { x, y }
          };
        });
        
        // 计算实际的最大列高度
        const maxColumnHeight = Math.max(...columnHeights);
        const totalHeight = maxColumnHeight - startY + framePadding * 2;

        // 先创建一个 shapeId
        const newShapeId = createShapeId();

        const iframeShape = {
          id: newShapeId,
          type: 'frame',
          x: startX - framePadding,
          y: startY - framePadding,
          props: {
            w: totalWidth + framePadding * 2,
            h: totalHeight,
            name: label,
          },
        }

        // 创建框架容器
        const frameShape = editor.createShape(iframeShape);

        frameId = frameShape.id as TLShapeId;
        createdShapeIds.push(frameId);
        setShapeId(newShapeId, iframeShape);
        
        // 将图片添加到框架中
        const imageShapes = positionedImages.map(img => {
          if (!img.position) {
            console.warn('Image missing position data', img.id);
            return null;
          }
          
          const shape = editor.createShape({
            type: 'image',
            x: img.position.x,
            y: img.position.y,
            parentId: frameId as TLParentId,
            props: {
              assetId: img.id,
              w: img.props.w,
              h: img.props.h,
            }
          });
          
          createdShapeIds.push(shape.id as TLShapeId);
          return shape;
        }).filter(Boolean);

        // 将框架容器置于最底层
        editor.sendToBack([frameId]);
        
        // 等待所有图片加载完成
        const imageElements = imageShapes.map(shape => {
          if (!shape) return null;
          const element = editor.getShape(shape.id as TLShapeId);
          return element && element.type === 'image' ? element as TLImageShape : null;
        }).filter(Boolean);
        
        if (imageElements.length === 0) {
          throw new Error('Failed to create image elements');
        }

        // 监听图片加载完成事件
        const checkImagesLoaded = (retryCount = 0, maxRetries = 30) => {
          if (retryCount >= maxRetries) {
            console.warn('Exceeded maximum retries waiting for images to load');
            return;
          }
          
          const allLoaded = imageElements.every(element => {
            if (!element) return false;
            const assetId = element.props.assetId;
            if (!assetId) return false;
            const asset = editor.getAsset(assetId as TLAssetId);
            return asset?.type === 'image' && !asset.props.isAnimated;
          });

          if (allLoaded) {
            // 计算实际的最大高度
            const actualHeights = imageElements.map(element => {
              if (!element) return 0;
              const assetId = element.props.assetId;
              if (!assetId) return 0;
              const asset = editor.getAsset(assetId as TLAssetId);
              if (!asset || asset.type !== 'image') return 0;
              return element.y + asset.props.h;
            });
            
            const maxActualHeight = Math.max(...actualHeights);
            const newTotalHeight = maxActualHeight - startY + framePadding * 2;

            // 更新框架高度
            if (frameId) {
              editor.updateShape({
                id: frameId as TLShapeId,
                type: 'frame',
                props: {
                  h: newTotalHeight
                }
              });
            }
          } else {
            // 如果还有图片未加载完成，继续检查
            checkImagesLoadedTimer = setTimeout(() => checkImagesLoaded(retryCount + 1, maxRetries), 100);
          }
        };

        checkImagesLoaded();

        // 创建完成后，将视图中心移动到新创建的内容
        editor.setCamera({
          x: editor.getCamera().x,
          y: startY + totalHeight / 2,
          z: editor.getCamera().z
        });

        return true;
      } catch (error) {
        console.error('Failed to create waterfall layout:', error);

        if (frameId) {
          try {
            editor.deleteShape(frameId);
          } catch (e) {
            console.error('Failed to clean up frame:', e);
          }
        }
        
        return false;
      } finally {
        if (checkImagesLoadedTimer) {
          clearTimeout(checkImagesLoadedTimer);
        }
      }
    },
    [editor]
  );

  return { createWaterfallLayout };
}; 